import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types';
import { isValidReturnUrl } from '@/utils/returnUrl';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Token storage keys
const ACCESS_TOKEN_KEY = 'pce-access-token';
const REMEMBER_ME_KEY = 'pce-remember-me';

// In-memory token (also persisted to localStorage for cross-tab access)
let accessToken: string | null = null;

// Callback to notify auth store when token is invalidated (avoids circular imports)
let onAuthInvalidated: (() => void) | null = null;
export const setOnAuthInvalidated = (callback: () => void) => {
  onAuthInvalidated = callback;
};

// Proactive token refresh settings
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

// Initialize token from storage on module load
function initializeToken(): void {
  // Always use localStorage for the token to ensure cross-tab access
  // The "remember me" flag controls whether we persist across browser sessions
  const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (storedToken) {
    accessToken = storedToken;
    // Schedule proactive refresh for the stored token
    scheduleTokenRefresh(storedToken);
  }
}

/**
 * Decode JWT token to extract payload (without verification)
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

/**
 * Schedule proactive token refresh before expiry
 */
function scheduleTokenRefresh(token: string): void {
  // Clear any existing scheduled refresh
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return;

  const expiryMs = payload.exp * 1000;
  const refreshAt = expiryMs - TOKEN_REFRESH_BUFFER_MS;
  const delay = refreshAt - Date.now();

  // Only schedule if there's time before expiry
  if (delay > 0) {
    refreshTimeoutId = setTimeout(() => {
      proactiveTokenRefresh();
    }, delay);
  } else if (delay > -TOKEN_REFRESH_BUFFER_MS) {
    // Token is about to expire or just expired - refresh immediately
    proactiveTokenRefresh();
  }
  // If token is long expired, let the 401 handler deal with it
}

/**
 * Proactively refresh the token before it expires
 */
async function proactiveTokenRefresh(): Promise<void> {
  if (isRefreshing) return; // Already refreshing

  try {
    isRefreshing = true;
    // No Authorization header needed - backend validates via refresh token cookie
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
      withCredentials: true,
    });

    const newToken = response.data?.accessToken || response.data?.data?.accessToken;
    if (newToken) {
      setAccessToken(newToken);
      onTokenRefreshed(newToken);
    }
  } catch (error) {
    // Proactive refresh failed - don't clear token yet
    // Let the 401 handler deal with it on next request
    console.error('Proactive token refresh failed', error);
  } finally {
    isRefreshing = false;
  }
}

// Initialize on module load
initializeToken();

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === ACCESS_TOKEN_KEY) {
      const newToken = event.newValue;
      const oldToken = accessToken;
      accessToken = newToken;

      if (newToken) {
        // Another tab refreshed the token - use it and reschedule
        scheduleTokenRefresh(newToken);
      } else if (oldToken) {
        // Token was cleared in another tab (logout)
        if (refreshTimeoutId) {
          clearTimeout(refreshTimeoutId);
          refreshTimeoutId = null;
        }
        // Only redirect to login if on a protected route (members/admin area).
        // Public pages should continue to work without authentication.
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/members') || currentPath.startsWith('/admin')) {
          const returnUrl = isValidReturnUrl(currentPath) ? encodeURIComponent(currentPath) : '';
          window.location.href = returnUrl ? `/login?returnUrl=${returnUrl}` : '/login';
        }
      }
    }
  });
}

export const setAccessToken = (token: string | null, rememberMe?: boolean): void => {
  accessToken = token;

  // Update remember me preference if provided
  if (rememberMe !== undefined) {
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, 'true');
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
  }

  if (token) {
    // Always store in localStorage for cross-tab access
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    // Schedule proactive refresh for the new token
    scheduleTokenRefresh(token);
  } else {
    // Clear token on logout
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
    // Cancel any scheduled refresh
    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId);
      refreshTimeoutId = null;
    }
  }
};

export const getAccessToken = (): string | null => {
  return accessToken;
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// Backend error response type (wraps ApiError in error property)
interface BackendErrorResponse {
  success: boolean;
  error?: ApiError;
  code?: string;
  message?: string;
  details?: Record<string, string[]>;
}

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<BackendErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - try to refresh token
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't try to refresh for auth endpoints themselves
      if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
          withCredentials: true,
        });

        const newToken = response.data?.accessToken || response.data?.data?.accessToken;
        if (newToken) {
          setAccessToken(newToken);
          onTokenRefreshed(newToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return apiClient(originalRequest);
        }

        // No token in response - treat as refresh failure
        throw new Error('No token in refresh response');
      } catch {
        // Refresh failed - clear stale token and notify auth store.
        // ProtectedRoute handles login redirects for member/admin pages.
        // Public pages continue to work without authentication.
        setAccessToken(null);
        onAuthInvalidated?.();

        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // Transform error for consistent handling
    // Backend ApiResponse wraps errors in an "error" property: { success: false, error: { code, message, details } }
    const backendError = error.response?.data?.error;
    const apiError: ApiError = {
      code: backendError?.code || error.response?.data?.code || 'UNKNOWN_ERROR',
      message: backendError?.message || error.response?.data?.message || error.message || 'An unexpected error occurred',
      details: backendError?.details || error.response?.data?.details,
      status: error.response?.status,
    };

    return Promise.reject(apiError);
  }
);

// Backend API response wrapper type
interface BackendApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
}

// Generic API response handler - unwraps backend ApiResponse wrapper
export async function handleApiResponse<T>(
  promise: Promise<{ data: BackendApiResponse<T> | T }>
): Promise<T> {
  const response = await promise;
  const body = response.data;

  // Check if this is a wrapped ApiResponse (has success property)
  if (body && typeof body === 'object' && 'success' in body) {
    const apiResponse = body as BackendApiResponse<T>;

    // Check for error response
    if (!apiResponse.success && apiResponse.error) {
      const error: ApiError = {
        code: apiResponse.error.code,
        message: apiResponse.error.message,
        details: apiResponse.error.details,
      };
      throw error;
    }

    // Check if this is an ActionResultResponse (has message at root level)
    // ActionResultResponse: { success, message, data? } - return whole object as ActionResult
    // ApiResponse<T>: { success, data } - unwrap and return data
    if ('message' in apiResponse && typeof (apiResponse as { message?: unknown }).message === 'string') {
      // This is an ActionResultResponse - return as-is (the whole object IS the result)
      return body as T;
    }

    // If it has a data property, return the data (ApiResponse<T>)
    // This includes null values - endpoints can intentionally return null (e.g., "no testimonial found")
    if ('data' in apiResponse) {
      return apiResponse.data as T;
    }

    // Non-generic ApiResponse (like { success: true }) - return as-is
    // This handles responses that have success but data is at root level (e.g., LoginResponse)
    return body as T;
  }

  // Not wrapped - return as-is
  return body as T;
}
